import { api } from '../../services/api';

export interface FacilitySummary {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  facility_type: 'SNF' | 'ALF' | null;
  status: string | null;
  licensed_beds: string | null;
  baa_signed: boolean | null;
  credentialing_status: string | null;
  last_visit_date: string | null;
}

export interface FacilityDetail extends FacilitySummary {
  address: string | null;
  zip_code: string | null;
  phone: string | null;
  email: string | null;
  facility_type_name: string | null;
  region: string | null;
  baa_sent: boolean | null;
  baa_sent_date: string | null;
  baa_signed_date: string | null;
  provider_next_visit_date: string | null;
  tm_next_follow_up_date: string | null;
  notes: string | null;
  patients: { total: number; active: number };
  scheduling: { total: number; upcoming: number };
  /** The Monday mirror's money (historical, 2026-10-20): `historical` says as of when, and warns. Absent from an older server. */
  finance: { total_sale_value: number; remaining_amount: number; amount_overdue: number; historical?: MirrorAsOf; source?: string };
}

/** "Monday historical mirror, as of <date>" (backend services/finance/mondayMirrorAsOf.js). */
export interface MirrorAsOf { as_of: string | null; as_of_day: string | null; before_sunset: boolean; historical: true; label: string; warning: string }

export interface DashboardSummary {
  facilities: { total: number; active: number; snf: number; alf: number; baa_signed: number; credentialed: number };
  patients: { total: number; active: number; prospect: number; inactive: number; deceased: number };
  scheduling: { upcomingVisits: number; total: number; last_30d: number; last_90d: number };
  finance: {
    total_accounts: number;
    total_sale_value: number;
    remaining_amount: number;
    amount_overdue: number;
    paid_accounts: number;
    past_due_accounts: number;
    historical?: MirrorAsOf;
    source?: string;
  };
  hearingAids: { total: number; delivered: number; byType: { hearing_aid_type: string; count: number }[] };
}

export interface ReadinessFacility {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  baa_signed: boolean | null;
  baa_sent: boolean | null;
  credentialing_status: string | null;
  last_visit_date: string | null;
  readiness_score: number;
}

export interface ReadinessSummary {
  averageScore: number;
  categories: { category: string; score: number }[];
  facilityCount: number;
  needsAttention: {
    missingBaa: ReadinessFacility[];
    noCredentialing: ReadinessFacility[];
    noVisit: ReadinessFacility[];
  };
}

export interface SyncStatusRow {
  target_table: string;
  row_count: number;
  last_synced_at: string;
}

interface ApiResult<T> {
  success: boolean;
  data?: T;
  pagination?: { page: number; pageSize: number; total: number };
  error?: string;
}

export const emergeApi = {
  getSummary: async (): Promise<ApiResult<DashboardSummary>> => (await api.get('/emerge/dashboard/summary')) as ApiResult<DashboardSummary>,
  getFacilities: async (params: { page?: number; pageSize?: number; q?: string } = {}): Promise<ApiResult<FacilitySummary[]>> => {
    const qp = new URLSearchParams();
    if (params.page) qp.set('page', String(params.page));
    if (params.pageSize) qp.set('pageSize', String(params.pageSize));
    if (params.q) qp.set('q', params.q);
    const qs = qp.toString();
    return (await api.get(`/emerge/dashboard/facilities${qs ? `?${qs}` : ''}`)) as ApiResult<FacilitySummary[]>;
  },
  getFacility: async (id: string): Promise<ApiResult<FacilityDetail>> => (await api.get(`/emerge/dashboard/facilities/${id}`)) as ApiResult<FacilityDetail>,
  getReadiness: async (): Promise<ApiResult<ReadinessSummary>> => (await api.get('/emerge/dashboard/readiness')) as ApiResult<ReadinessSummary>,
  getSyncStatus: async (): Promise<ApiResult<SyncStatusRow[]>> => (await api.get('/emerge/dashboard/sync-status')) as ApiResult<SyncStatusRow[]>,
};
